import {
  BillingEntityTypeEnum,
  BillingTransactionTypeEnum,
  CustomerProfileEntry,
  CustomerProfileEntryKind,
} from '@api/READ_ONLY/billing_api/Api';
import { AppDataTable } from '@app/components/common/AppDataTable';
import TableLink from '@app/components/common/TableLink';
import { getRecordTypeLabelKey } from '@app/features/billing/CustomerProfile/recordTypeLabels';
import { isStaffPayoutType } from '@app/features/billing/Payments/staffTypes';
import type { GenericDataTableApiParams, GenericDataTableResponse, TableColumn } from '@konnecta/generic-table';
import { APP_PREFIX, VISITS_PAGE } from '@app/routes/urls';
import { currencyFormatter, type SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Popover, Tooltip } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiInfo, FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import { MdOutlineBadge, MdOutlinePerson } from 'react-icons/md';

export type LedgerRow = {
  id: string;
  paymentId: number;
  paymentIdentifier: string;
  entityIdentifier: number;
  entityName?: string | null;
  date: string;
  visitId?: number | null;
  recordType: BillingTransactionTypeEnum;
  kind: CustomerProfileEntryKind;
  notes?: string;
  amount: number;
  runningTotal?: number | null;
};

// actions is the edit/delete menu; every other column is exported.
export const NOT_EXPORTED_CUSTOMER_PROFILE = ['actions'] as const;

type CustomerProfileLedgerTableProps = {
  entityType: BillingEntityTypeEnum;
  currency: SupportedCurrencies;
  locale: string;
  emptyText?: ReactNode;
  fetchData: (params: GenericDataTableApiParams) => Promise<GenericDataTableResponse<CustomerProfileEntry>>;
  /** Identifies the ledger being shown; a change refetches it. */
  refreshToken: string;
  /** Without it the table offers no export, as when no entity is selected. */
  getExportParams?: () => Record<string, unknown>;
  onDataChange?: (rows: LedgerRow[]) => void;
  onEdit: (row: LedgerRow) => void;
  onDelete: (row: LedgerRow) => void;
  onVisitClick: (visitId: number) => void;
};

// Paging stays out of the URL and storage so a remount for a new ledger starts on page 1.
const LEDGER_STORAGE = { enableUrlSync: false, enableLocalStorage: false };

const LEDGER_FEATURES = { filtering: false, sorting: false, columnVisibility: false, columnReordering: false };

// An unknown type is kept as-is (shown raw, not editable) rather than relabelled a client payment.
const mapRecordType = (recordType: string): BillingTransactionTypeEnum => recordType as BillingTransactionTypeEnum;

const isEditableRecordType = (recordType: LedgerRow['recordType']) =>
  recordType === BillingTransactionTypeEnum.ClientPayment || isStaffPayoutType(recordType);

export const toLedgerRow = (entry: CustomerProfileEntry): LedgerRow => ({
  id: entry.identifier ?? String(entry.recordId),
  paymentId: entry.recordId,
  paymentIdentifier: entry.identifier ?? String(entry.recordId),
  entityIdentifier: entry.entityId,
  entityName: entry.entityName ?? null,
  date: entry.date,
  visitId: entry.visitId ?? null,
  recordType: mapRecordType(entry.recordType),
  kind: entry.kind,
  notes: entry.notes ?? '',
  amount: entry.amount,
  runningTotal: entry?.runningTotal ?? null,
});

const isAutomaticRecordType = (recordType: LedgerRow['recordType']) =>
  recordType === BillingTransactionTypeEnum.ClientCharge ||
  recordType === BillingTransactionTypeEnum.DoctorAccrual ||
  recordType === BillingTransactionTypeEnum.CaregiverAccrual ||
  recordType === BillingTransactionTypeEnum.ProviderAccrual ||
  // Cash-register movements are driven by the cash-register flow, not editable here.
  recordType === BillingTransactionTypeEnum.CashCollection ||
  recordType === BillingTransactionTypeEnum.CashDeposit;

const getAmountColorClass = (recordType: LedgerRow['recordType']) => {
  if (
    recordType === BillingTransactionTypeEnum.ClientPayment ||
    // A deposit hands cash back, reducing what the caregiver owes — same as a payment.
    recordType === BillingTransactionTypeEnum.CashDeposit
  ) {
    return 'text-emerald-600';
  }

  if (
    recordType === BillingTransactionTypeEnum.DoctorPayout ||
    recordType === BillingTransactionTypeEnum.CaregiverPayout ||
    recordType === BillingTransactionTypeEnum.ProviderPayout
  ) {
    return 'text-rose-500';
  }

  if (isAutomaticRecordType(recordType)) {
    return 'text-slate-500';
  }

  return 'text-slate-600';
};

const getCreditDebitAmounts = (row: LedgerRow): { credit: number | null; debit: number | null } => {
  if (row.recordType === BillingTransactionTypeEnum.ClientPayment) {
    return { credit: Math.abs(row.amount), debit: null };
  }

  if (row.recordType === BillingTransactionTypeEnum.ClientCharge) {
    return { credit: null, debit: Math.abs(row.amount) };
  }

  // Cash collected behaves like a charge — it goes in the debit ("collected") column.
  if (row.recordType === BillingTransactionTypeEnum.CashCollection) {
    return { credit: null, debit: Math.abs(row.amount) };
  }

  // Cash deposited/handed back behaves like a payment — it goes in the credit column.
  if (row.recordType === BillingTransactionTypeEnum.CashDeposit) {
    return { credit: Math.abs(row.amount), debit: null };
  }

  if (
    row.recordType === BillingTransactionTypeEnum.DoctorPayout ||
    row.recordType === BillingTransactionTypeEnum.CaregiverPayout ||
    row.recordType === BillingTransactionTypeEnum.ProviderPayout
  ) {
    return { credit: null, debit: Math.abs(row.amount) };
  }

  if (
    row.recordType === BillingTransactionTypeEnum.DoctorAccrual ||
    row.recordType === BillingTransactionTypeEnum.CaregiverAccrual ||
    row.recordType === BillingTransactionTypeEnum.ProviderAccrual
  ) {
    return { credit: Math.abs(row.amount), debit: null };
  }

  if (row.amount >= 0) {
    return { credit: null, debit: Math.abs(row.amount) };
  }

  return { credit: Math.abs(row.amount), debit: null };
};

const formatSignedCurrency = (
  value: number,
  currency: SupportedCurrencies,
  locale: string,
  includeSign: boolean = false
) => {
  if (!includeSign || value === 0) {
    return `${currencyFormatter(Math.abs(value), currency, locale)}`;
  }

  return `${value > 0 ? '+' : '-'}${currencyFormatter(Math.abs(value), currency, locale)}`;
};

type ColumnHeaderWithInfoProps = {
  label: string;
  infoText: string;
};

const ColumnHeaderWithInfo = ({ label, infoText }: ColumnHeaderWithInfoProps) => (
  <div className="inline-flex items-center gap-1.5">
    <span>{label}</span>
    <Tooltip
      placement="top"
      title={<span className="whitespace-pre-line">{infoText}</span>}
      styles={{
        root: { maxWidth: 360 },
      }}
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-slate-600"
        aria-label={`${label} info`}
      >
        <FiInfo size={12} />
      </button>
    </Tooltip>
  </div>
);

type LedgerColumnOptions = {
  t: TFunction;
  entityType: BillingEntityTypeEnum;
  currency: SupportedCurrencies;
  locale: string;
  onEdit: (row: LedgerRow) => void;
  onDelete: (row: LedgerRow) => void;
  onVisitClick: (visitId: number) => void;
};

export const getLedgerColumns = ({
  t,
  entityType,
  currency,
  locale,
  onEdit,
  onDelete,
  onVisitClick,
}: LedgerColumnOptions): TableColumn<LedgerRow>[] => {
  const isClientLedger = entityType === BillingEntityTypeEnum.Client;
  const isCashRegisterLedger = entityType === BillingEntityTypeEnum.CaregiverCashRegister;
  const creditLabel = isCashRegisterLedger
    ? t('cash-handed-back')
    : isClientLedger
      ? t('ledger-column-payments')
      : t('ledger-column-earnings');
  const debitLabel = isCashRegisterLedger
    ? t('cash-collected')
    : isClientLedger
      ? t('ledger-column-charges')
      : t('ledger-column-payments');
  const creditInfoText = isCashRegisterLedger
    ? t('ledger-column-cash-handed-back-info')
    : isClientLedger
      ? t('ledger-column-payments-client-info')
      : t('ledger-column-earnings-staff-info');
  const debitInfoText = isCashRegisterLedger
    ? t('ledger-column-cash-collected-info')
    : isClientLedger
      ? t('ledger-column-charges-client-info')
      : t('ledger-column-payments-staff-info');
  const balanceInfoText = `${t('ledger-column-balance-client-info')}\n${t('ledger-column-balance-staff-info')}`;

  return [
    {
      id: 'date',
      header: t('Date'),
      accessor: 'date',
      width: 150,
      sortable: false,
      copy: false,
      cellRenderer: (value, record) => {
        const formattedDate = dayjs(value as string).format('DD MMM YYYY HH:mm');
        const visitId = record.visitId;

        if (!visitId) {
          return <span className="text-slate-600">{formattedDate}</span>;
        }

        return (
          <TableLink
            to={`/${APP_PREFIX}/${VISITS_PAGE}/${visitId}/edit`}
            onClick={() => onVisitClick(visitId)}
            title={t('ledger-col-edit-visit-title')}
            className="text-blue-600"
          >
            <span className="text-blue-600">{formattedDate}</span>
          </TableLink>
        );
      },
    },
    {
      id: 'recordType',
      header: t('ledger-col-entry-type'),
      accessor: 'recordType',
      width: 180,
      sortable: false,
      copy: false,
      cellRenderer: (_value, record) => {
        const isClientRecord =
          record.recordType === BillingTransactionTypeEnum.ClientPayment ||
          record.recordType === BillingTransactionTypeEnum.ClientCharge;
        const isAutomatic = isAutomaticRecordType(record.recordType);

        const borderClass = isAutomatic
          ? 'border-slate-300'
          : isClientRecord
            ? 'border-emerald-300'
            : 'border-blue-300';

        const textClass = isAutomatic ? 'text-slate-500' : isClientRecord ? 'text-emerald-500' : 'text-blue-500';

        const icon = isClientRecord ? <MdOutlinePerson className="text-sm" /> : <MdOutlineBadge className="text-sm" />;
        const labelKey = getRecordTypeLabelKey(record.recordType);

        return (
          <div className="flex items-center gap-3">
            <span
              className={`flex h-[22px] w-[22px] items-center justify-center rounded-md border bg-white ${borderClass} ${textClass}`}
            >
              {icon}
            </span>
            <span className="text-sm font-medium text-slate-700">{t(labelKey)}</span>
          </div>
        );
      },
    },
    {
      id: 'credit',
      header: creditLabel,
      headerRenderer: () => <ColumnHeaderWithInfo label={creditLabel} infoText={creditInfoText} />,
      accessor: 'amount',
      align: 'right',
      width: 140,
      sortable: false,
      copy: false,
      cellRenderer: (_value, record) => {
        const { credit } = getCreditDebitAmounts(record);

        if (credit == null) {
          return <span className="text-slate-400">—</span>;
        }

        return (
          <span className={`font-medium tabular-nums ${getAmountColorClass(record.recordType)}`}>
            {formatSignedCurrency(credit, currency, locale)}
          </span>
        );
      },
    },
    {
      id: 'debit',
      header: debitLabel,
      headerRenderer: () => <ColumnHeaderWithInfo label={debitLabel} infoText={debitInfoText} />,
      accessor: 'amount',
      align: 'right',
      width: 140,
      sortable: false,
      copy: false,
      cellRenderer: (_value, record) => {
        const { debit } = getCreditDebitAmounts(record);

        if (debit == null) {
          return <span className="text-slate-400">—</span>;
        }

        return (
          <span className={`font-medium tabular-nums ${getAmountColorClass(record.recordType)}`}>
            {formatSignedCurrency(debit, currency, locale)}
          </span>
        );
      },
    },
    {
      id: 'runningTotal',
      header: t('balance'),
      headerRenderer: () => <ColumnHeaderWithInfo label={t('balance')} infoText={balanceInfoText} />,
      accessor: 'runningTotal',
      align: 'right',
      width: 160,
      sortable: false,
      copy: false,
      cellRenderer: (value) => {
        const runningTotal = value as number | null | undefined;

        if (runningTotal == null || !Number.isFinite(runningTotal)) {
          return <span className="text-slate-400">—</span>;
        }

        return (
          <span className="font-medium tabular-nums text-slate-700">
            {formatSignedCurrency(runningTotal, currency, locale, true)}
          </span>
        );
      },
    },
    {
      id: 'notes',
      header: t('notes'),
      accessor: 'notes',
      sortable: false,
      copy: false,
      cellRenderer: (value) => {
        const notes = value as string | undefined;
        if (!notes?.trim()) {
          return <span className="text-slate-400">—</span>;
        }

        if (notes.length <= 80) {
          return <span className="text-slate-600">{notes}</span>;
        }

        return (
          <div className="flex items-center gap-2">
            <span className="max-w-[320px] truncate text-slate-600">{notes}</span>
            <Popover
              trigger="click"
              placement="topLeft"
              content={<div className="max-w-[360px] whitespace-pre-wrap text-sm text-slate-600">{notes}</div>}
            >
              <Button
                type="text"
                size="small"
                icon={<FiInfo className="text-slate-400" />}
                className="h-6 w-6 p-0 hover:!bg-slate-100"
                aria-label={t('notes')}
              />
            </Popover>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: t('actions'),
      accessor: () => undefined,
      align: 'center',
      width: 80,
      sortable: false,
      copy: false,
      cellRenderer: (_value, record) => {
        if (!isEditableRecordType(record.recordType)) {
          return null;
        }

        const items: MenuProps['items'] = [
          { key: 'edit', label: t('edit'), icon: <FiEdit2 /> },
          { key: 'delete', label: t('delete'), icon: <FiTrash2 />, danger: true },
        ];

        const onMenuClick: MenuProps['onClick'] = ({ key }) => {
          if (key === 'edit') {
            onEdit(record);
            return;
          }

          if (key === 'delete') {
            onDelete(record);
          }
        };

        return (
          <Dropdown trigger={['click']} menu={{ items, onClick: onMenuClick }}>
            <Button type="text" size="small" aria-label={t('tbl-row-actions-aria')}>
              <FiMoreVertical />
            </Button>
          </Dropdown>
        );
      },
    },
  ];
};

const CustomerProfileLedgerTable = ({
  entityType,
  currency,
  locale,
  emptyText,
  fetchData,
  refreshToken,
  getExportParams,
  onDataChange,
  onEdit,
  onDelete,
  onVisitClick,
}: CustomerProfileLedgerTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => getLedgerColumns({ t, entityType, currency, locale, onEdit, onDelete, onVisitClick }),
    [t, entityType, currency, locale, onEdit, onDelete, onVisitClick]
  );

  const config = useMemo(
    () => ({
      tableId: 'customer-profile-ledger',
      rowKey: 'id' as const,
      storage: LEDGER_STORAGE,
      features: LEDGER_FEATURES,
      columns,
      fetchData,
      transformData: toLedgerRow,
    }),
    [columns, fetchData]
  );
  const emptyState = useMemo(() => (emptyText ? { render: () => emptyText } : undefined), [emptyText]);

  return (
    <AppDataTable<CustomerProfileEntry, LedgerRow>
      config={config}
      refreshToken={refreshToken}
      emptyState={emptyState}
      onDataChange={onDataChange}
      export={
        getExportParams
          ? { list: 'customer_profile', getParams: getExportParams, notExported: [...NOT_EXPORTED_CUSTOMER_PROFILE] }
          : undefined
      }
    />
  );
};

export default CustomerProfileLedgerTable;
